import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function CalendarView() {
  const { business } = useAuth();
  const [cursor, setCursor] = useState(new Date());
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState(new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState(null);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const loadMonth = useCallback(async () => {
    try {
      const { data } = await api.get("/reservations");
      const c = {};
      data.forEach((r) => { if (r.date && r.status !== "cancellata") c[r.date] = (c[r.date] || 0) + 1; });
      setCounts(c);
    } catch (e) { toast.error(apiErr(e)); }
  }, []);

  const loadAvailability = useCallback(async (d) => {
    if (!business) return;
    try {
      if (business.mode === "slot") {
        const { data } = await api.get(`/business/public/${business.slug}/availability`, { params: { date: d } });
        setSlots(data.slots);
      } else {
        const co = new Date(new Date(d).getTime() + 86400000).toISOString().slice(0, 10);
        const { data } = await api.get(`/business/public/${business.slug}/availability`, { params: { checkin: d, checkout: co } });
        setSlots(data.rooms.map((r) => ({ time: r.name, available: r.available ? r.capacity : 0, total: r.capacity })));
      }
    } catch { setSlots([]); }
  }, [business]);

  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => { loadAvailability(selected); }, [selected, loadAvailability]);

  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const iso = (d) => new Date(year, month, d).toLocaleDateString("en-CA");

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-primary">Calendario</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Disponibilità</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 rounded-lg hover:bg-muted" data-testid="calendar-prev"><ChevronLeft className="h-5 w-5" /></button>
            <p className="font-heading font-bold text-lg capitalize">{cursor.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</p>
            <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 rounded-lg hover:bg-muted" data-testid="calendar-next"><ChevronRight className="h-5 w-5" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-400 mb-2">{WD.map((d) => <div key={d}>{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const dateStr = iso(d);
              const cnt = counts[dateStr] || 0;
              const isSel = dateStr === selected;
              const isToday = dateStr === new Date().toLocaleDateString("en-CA");
              return (
                <button key={i} onClick={() => setSelected(dateStr)} data-testid={`calendar-day-${dateStr}`}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm transition-colors ${isSel ? "bg-primary text-white" : "hover:bg-muted"} ${isToday && !isSel ? "ring-2 ring-primary/40" : ""}`}>
                  <span className={isSel ? "font-bold" : "text-slate-700"}>{d}</span>
                  {cnt > 0 && <span className={`mt-0.5 h-1.5 w-1.5 rounded-full ${isSel ? "bg-white" : "bg-primary"}`} />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
          <p className="font-semibold text-slate-900 mb-1 capitalize">{new Date(selected).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</p>
          <p className="text-xs text-slate-500 mb-3">{business?.mode === "slot" ? "Posti disponibili per orario" : "Camere disponibili"}</p>
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto hide-scrollbar">
            {slots === null ? <p className="text-slate-400 text-sm">Caricamento...</p> :
              slots.length === 0 ? <p className="text-slate-400 text-sm">Nessuna configurazione.</p> :
              slots.map((s, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-muted px-3 py-2 text-sm" data-testid={`calendar-slot-${i}`}>
                  <span className="font-medium text-slate-700">{s.time}</span>
                  <span className={`text-xs font-semibold ${s.available > 0 ? "text-emerald-600" : "text-destructive"}`}>{s.available > 0 ? `${s.available} liberi` : "Completo"}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
